import type { Location, Route } from 'vue-router';

export const RoutePath = {
  Index: '/',
  Home: '/home',
  Settings: '/settings',
  Discover: '/discover',
  Profile: '/profile',
  UserHistory: '/profile/history',
  UserDetails: '/profile/details',
  Login: '/profile/login',
  Live: '/live',
  Search: '/search',
  Welcome: '/welcome',
  Episode: '/episode/:videoId',
  Player: '/player/:videoId',
  DeeplinkLive: '/deeplink/live/:channel',
  Series: '/series/:slugOrVID',
  Category: '/category/:slug',
} as const;

export type RoutePath = (typeof RoutePath)[keyof typeof RoutePath];


export const RouteName = {
  Index: 'Index',
  Home: 'Home',
  Rails: 'Rails',
  Settings: 'Settings',
  Discover: 'Discover',
  Profile: 'Profile',
  UserBookmarks: 'UserBookmarks',
  UserHistory: 'UserHistory',
  UserDetails: 'UserDetails',
  Login: 'Login',
  Live: 'Live',
  Search: 'Search',
  Welcome: 'Welcome',
  Episode: 'Episode',
  Player: 'Player',
  DeeplinkLive: 'DeeplinkLive',
  Series: 'Series',
  Category: 'Category',
} as const;

export type RouteName = (typeof RouteName)[keyof typeof RouteName];

/**
 * Typed route locations.
 *
 * The param names below are derived from the path literals in `RoutePath`, so
 * there is no second list to keep in sync: renaming `:videoId` in
 * `RoutePath.Episode` immediately breaks every `routeTo(RouteName.Episode, ...)`
 * call that still passes the old name.
 *
 * This exists because `router.push({ name, params })` is unchecked by
 * vue-router's own types — `params` is `Record<string, string>`, so a wrong or
 * missing key is only discovered at runtime, where it aborts the navigation
 * with a rejected promise and no visible error on a TV.
 */

/** Pulls `:param` names out of a path literal: '/episode/:videoId' -> 'videoId'. */
type PathParams<P extends string> =
  P extends `${string}:${infer Param}/${infer Rest}`
    ? Param | PathParams<Rest>
    : P extends `${string}:${infer Param}`
      ? Param
      : never;

/**
 * Names that have a path above. The two that do not (Rails, UserBookmarks) are
 * nested children whose paths live in the router config; both are param-less,
 * so resolving them to `never` here is correct.
 */
type PathFor<N extends RouteName> = N extends keyof typeof RoutePath
  ? (typeof RoutePath)[N]
  : never;

export type ParamsFor<N extends RouteName> = PathParams<PathFor<N>>;

export interface LocationExtras {
  query?: Record<string, string>;
  hash?: string;
}

/**
 * Params are required only for routes whose path declares them, and forbidden
 * for the rest — so `routeTo(RouteName.Home)` and
 * `routeTo(RouteName.Episode, { videoId })` are both single-argument-correct.
 */
type Args<N extends RouteName> = [ParamsFor<N>] extends [never]
  ? [extras?: LocationExtras]
  : [params: Record<ParamsFor<N>, string>, extras?: LocationExtras];

/**
 * Builds a location for `router.push` / `router.replace` / `<router-link :to>`.
 *
 * Prefer this over a hand-built path string: `push({ path: `/episode/${id}` })`
 * type-checks no matter what the route actually looks like.
 */
export function routeTo<N extends RouteName>(name: N, ...args: Args<N>): Location {
  // Whether this route takes params is read off the same literal the types are
  // derived from, so the runtime arity matches the compile-time arity.
  const path: string | undefined = RoutePath[name as keyof typeof RoutePath];
  const takesParams = path?.includes(':') ?? false;

  const params = takesParams ? (args[0] as Record<string, string>) : undefined;
  const extras = (takesParams ? args[1] : (args[0] as LocationExtras | undefined)) ?? {};

  return { name, params, query: extras.query, hash: extras.hash };
}

/**
 * The param names a route declares, at runtime — the read-side counterpart to
 * `PathParams`, derived from the same literal so the two cannot disagree.
 *
 * Deliberately `split`/`startsWith` rather than `matchAll`, which needs
 * Chromium 73 and would break on the Tizen 4 / webOS 5 floor this app ships to.
 */
const paramNamesOf = (name: RouteName): string[] => {
  const path: string | undefined = RoutePath[name as keyof typeof RoutePath];
  if (!path) return [];

  const names: string[] = [];
  for (const segment of path.split('/')) {
    if (segment.startsWith(':')) names.push(segment.slice(1));
  }
  return names;
};

/**
 * Reads a route's declared params off `route`, typed by name.
 *
 * Returns `undefined` rather than throwing when the route is not `name`, or
 * when any declared param is absent — both of which a malformed deep link can
 * cause, and neither of which should take down a view on a TV. That makes the
 * single `if (!params)` check at a view's entry point the whole guard:
 *
 *   const params = paramsOf(RouteName.Episode, route);
 *   if (!params) { ... }
 *   await fetchVideo(params.videoId);   // typed, and known to be present
 */
export function paramsOf<N extends RouteName>(
  name: N,
  route: Route | undefined
): Record<ParamsFor<N>, string> | undefined {
  if (!route || route.name !== name) return undefined;

  const params: Record<string, string> = {};
  for (const paramName of paramNamesOf(name)) {
    const value = route.params[paramName];
    // A declared param with no value means the URL did not match the route as
    // written; treat it as a failed navigation rather than passing "" onward.
    if (!value) return undefined;
    params[paramName] = value;
  }

  return params as Record<ParamsFor<N>, string>;
}
